import { useState, useMemo, useCallback } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { fetchCashFlowData } from "../utils/cashFlowFetcher";
import { processCashflowTimeline } from "../utils/cashFlowParsers";

const todayStr = () => new Date().toISOString().split("T")[0];

const getStartOfMonth = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
};

/**
 * useCashFlow
 * Fluxo de Caixa Limpo - Padrão Clean Code.
 * Delega o heavy lifting de queries para o fetcher e a matemática para o Parser Puro.
 */
export const useCashFlow = () => {
    const { idTenant } = useTenant();

    const [startDate, setStartDate] = useState(getStartOfMonth());
    const [endDate, setEndDate] = useState(todayStr());

    const [bankAccounts, setBankAccounts] = useState([]);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);

    // Saldo Consolidado Atual (soma de todas as contas)
    const currentTotalBalance = useMemo(() => {
        return bankAccounts.reduce((acc, bank) => acc + (bank.balance || 0), 0);
    }, [bankAccounts]);

    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = useCallback(() => {
        if (!idTenant || !startDate || !endDate) return;

        let isMounted = true;
        setLoading(true);
        setHasSearched(true);

        fetchCashFlowData(idTenant, startDate, endDate)
            .then(({ bankAccounts, records }) => {
                if (!isMounted) return;
                setBankAccounts(bankAccounts);
                setRecords(records);
            })
            .catch((error) => {
                console.error("[useCashFlow] Erro ao carregar dados:", error);
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => { isMounted = false; };
    }, [idTenant, startDate, endDate]);

    // Agrupar registros por dia e calcular progressão de saldo delegando para o Parser PURO.
    const projectedDays = useMemo(() => {
        if (!hasSearched) return [];
        return processCashflowTimeline(records, currentTotalBalance, startDate);
    }, [records, currentTotalBalance, startDate, hasSearched]);

    return {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        bankAccounts,
        currentTotalBalance,
        projectedDays,
        records,
        loading,
        hasSearched,
        handleSearch
    };
};
